import { useIntl } from 'react-intl';
import { useQuery, UseQueryResult } from 'react-query';
import { HeaderData } from 'adapters/types';
import { useClient } from 'components/ClientProvider';
import { getDepartmentName, getStoreId } from 'components/StoreSelect/slice';
import { setSuccessMessage } from 'components/SuccessMessage/slice';
import { IError, onErrorHandler } from 'hooks/helper';
import { setSelectedVendors } from 'pages/VendorsTablePage/components/PrintChecks';
import { messages } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import {
  PrintChecksResponse,
  PrintChecksVendorData
} from 'pages/VendorsTablePage/components/PrintChecks/types';
import { errors } from 'pages/VendorsTablePage/messages';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  SELECT_ALL_CHECKS_PAGE_NO,
  SELECT_ALL_CHECKS_PAGE_SIZE,
  SORT_ORDER_PARAMS,
  SORT_PRINT_CHECKS_COLUMN
} from '../constants';
import { PrintChecksSelectAllType } from '../types';

export const useSelectAllPrintChecks = ({
  searchValue,
  isSelectAll,
  columnState
}: PrintChecksSelectAllType): UseQueryResult<PrintChecksResponse, IError> => {
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const storeId = useAppSelector(getStoreId);
  const selectedDepartmentName = useAppSelector(getDepartmentName);
  const headers: HeaderData = {
    ...client?.headers,
    'x-department-name': selectedDepartmentName
  };
  const sortColumn = columnState
    ? SORT_PRINT_CHECKS_COLUMN[columnState?.colId]
    : undefined;
  const sortOrder = columnState
    ? SORT_ORDER_PARAMS[columnState?.sort]
    : undefined;

  return useQuery(
    ['selectAllPrintChecks', headers, storeId],
    () =>
      client?.data.fetchHighVolumePrintAuthorizedVendors(
        headers,
        storeId,
        SELECT_ALL_CHECKS_PAGE_NO,
        SELECT_ALL_CHECKS_PAGE_SIZE,
        searchValue,
        sortColumn,
        sortOrder
      ),
    {
      enabled: isSelectAll,
      onSuccess: data => {
        const rowData: PrintChecksVendorData[] =
          data?.vendors.map(vendorData => ({
            ...vendorData,
            vendorNo: vendorData.vendorNo,
            vendorName: vendorData.vendorName,
            invoices: vendorData.invoices,
            netAmount: vendorData.totalNetAmount
          })) ?? [];
        dispatch(setSelectedVendors(rowData));
        if (rowData?.length > SELECT_ALL_CHECKS_PAGE_SIZE) {
          dispatch(
            setSuccessMessage(
              intl.formatMessage(messages.bulkChecksPrinting, {
                maxChecks: SELECT_ALL_CHECKS_PAGE_SIZE
              })
            )
          );
        }
      },
      onError: (error: IError) =>
        onErrorHandler(
          error,
          dispatch,
          intl,
          intl.formatMessage(errors.vendorApiFailureMsg)
        )
    }
  );
};
